import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Check, CreditCard, Building, Users, Stethoscope, 
  Mic, Shield, Clock, ArrowRight, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

export default function BillingPlans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    loadPlans();
    if (user) {
      loadCurrentSubscription();
    }
  }, [user]);

  const loadPlans = async () => {
    try {
      const response = await api.get('/billing/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const response = await api.get('/billing/subscription');
      setCurrentSubscription(response.data);
    } catch (error) {
      // User might not have a subscription yet
      console.log('No current subscription');
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate('/portal');
      return;
    }

    setCheckoutLoading(planId);
    
    try {
      const response = await api.post('/billing/create-checkout-session', {
        plan_id: planId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Building className="w-6 h-6" />;
      case 'professional': return <Stethoscope className="w-6 h-6" />;
      case 'enterprise': return <Mic className="w-6 h-6" />;
      default: return <Shield className="w-6 h-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'starter': return 'border-blue-200 bg-blue-50';
      case 'professional': return 'border-purple-300 bg-purple-50 ring-2 ring-purple-400';
      case 'enterprise': return 'border-amber-200 bg-amber-50';
      default: return 'border-gray-200';
    }
  };

  const isCurrentPlan = (planId) => {
    return currentSubscription?.plan?.id === planId && currentSubscription?.status === 'active';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select the plan that best fits your home care agency's needs. 
            All plans include a 14-day free trial.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative p-8 transition-all hover:shadow-xl ${getPlanColor(plan.id)}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.id === 'professional' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-lg ${
                  plan.id === 'starter' ? 'bg-blue-100 text-blue-600' :
                  plan.id === 'professional' ? 'bg-purple-100 text-purple-600' :
                  'bg-amber-100 text-amber-600'
                }`}>
                  {getPlanIcon(plan.id)}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{plan.name}</h2>
              </div>

              {/* Price */}
              <div className="mb-2">
                <span className="text-4xl font-bold text-gray-900">${plan.base_price}</span>
                <span className="text-gray-500">/month base</span>
              </div>
              <div className="mb-6 text-sm text-gray-600">
                <span className="font-medium">+${plan.per_user_price}</span> per active caregiver/month
              </div>

              {/* Target */}
              {plan.target && (
                <p className="text-sm text-gray-500 mb-4 italic">{plan.target}</p>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {isCurrentPlan(plan.id) ? (
                <Button 
                  className="w-full" 
                  variant="outline"
                  disabled
                >
                  <Check className="w-4 h-4 mr-2" />
                  Current Plan
                </Button>
              ) : (
                <Button
                  className={`w-full ${
                    plan.id === 'professional' 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : ''
                  }`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={checkoutLoading === plan.id}
                  data-testid={`subscribe-btn-${plan.id}`}
                >
                  {checkoutLoading === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>
              )}
            </Card>
          ))}
        </div>

        {/* Features Comparison */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            All Plans Include
          </h3>
          <div className="flex flex-wrap justify-center gap-6 text-gray-600">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-500" />
              <span>24/7 Data Access</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              <span>14-Day Free Trial</span>
            </div>
          </div>
        </div>

        {/* Back to Dashboard */}
        {user && (
          <div className="mt-8 text-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
            >
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
